import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import SummaryCards from "components/SummaryCards";
import { get, post } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import CustomPagination from "components/CustomPagination";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import InvoiceOverviewTemplate from "./InvoiceTemplates/InvoiceOverviewTemplate";
import { formattedDateMonth } from "helpers/utils/dateUtils";
import { showErrorToast } from "helpers/toastMessages";

const InvoiceLanding = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    overdue: 0,
    drafts: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const timerRef = useRef(null);
  const [count, setCount] = useState({
    totalAmount: 0,
    amountPaid: 0,
    approvedAmount: 0,
    overDueAmount: 0,
    rejectedAmount: 0,
    approved: 0,
    draft: 0,
    overdue: 0,
    overpaid: 0,
    overpaidAmount: 0,
    paid: 0,
    partially_PAID: 0,
    rejected: 0,
    send: 0,
    sent: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: "invoiceCode",
    direction: "ASC",
  });

  const [customers, setCustomers] = useState([]);
  console.log("cus", customers);

  const fetchInvoices = useCallback(
    async (query = "", page = 1) => {
      try {
        const response = await post(api.GET_ALL_INVOICES, {
          page: page - 1,
          pageSize: 10,
          sortBy: sortConfig.sortBy,
          searchKey: query,
          direction: sortConfig.direction,
          invoiceDTO: {
            status: null,
          },
        });
        if (response?.paginationResponse?.data) {
          const invoiceData = response.paginationResponse.data;
          setInvoices(invoiceData);
          setTotalPages(response?.paginationResponse?.totalPages || 0);
        } else {
          setInvoices([]);
          setTotalPages(0);
        }
      } catch (error) {
        console.error("Error fetching invoices:", {
          message: error.message,
          stack: error.stack,
          response: error.response?.data,
          status: error.response?.status,
        });
        setInvoices([]);
        setTotalPages(0);
      }
    },
    [sortConfig]
  );

  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.sortBy === columnName) {
        return {
          sortBy: columnName,
          direction: prevConfig.direction === "ASC" ? "DESC" : "ASC",
        };
      }
      return {
        sortBy: columnName,
        direction: "ASC",
      };
    });
  }, []);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  const fetchCustomers = useCallback(async () => {
    try {
      // setApiStatus((prev) => ({ ...prev, loading: true }));
      const response = await get(api.GET_ALL_CUSTOMERS_LIGHT_API);
      console.log("Customers API Response:", response);

      const updatedCustData = response.map((item) => ({
        value: String(item.customerId) || "",
        label:
          item.companyName.trim() !== ""
            ? `${item.companyName.trim()}`
            : `${item.email || item.customerCode || "N/A"} `,
        companyName: item.companyName || "",
        primaryContactName: item.primaryContactName || "",
      }));
      console.log("Formatted Customers:", updatedCustData);
      setCustomers(updatedCustData);
    } catch (error) {
      console.error("Fetch Customers Error:", error);
      showErrorToast("Failed to load customers");
      // setApiStatus((prev) => ({ ...prev, error: "Failed to load customers" }));
      setCustomers([]);
    }
  }, []);

  const columnSortMap = {
    "Invoice #": "invoiceCode",
    Customer: "invoiceCustomerDTO.name",
    "Invoice Date": "invoiceDate",
    "Total Amount": "total",
    "Amount Paid": "advanceAmount",
    "Due Amount": "dueAmount",
    "Due Date": "dueDate",
    Currency: "currency",
    Status: "status",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Invoice #",
          "Customer",
          "Invoice Date",
          "Total Amount",
          "Amount Paid",
          "Due Amount",
          "Due Date",
          "Currency",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchInvoices(searchQuery, page);
    },
    [fetchInvoices, searchQuery]
  );

  const handleSearchQuery = useCallback(
    (e) => {
      const query = e.target.value;
      setSearchQuery(query);

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        fetchInvoices(query);
      }, 300);
    },
    [fetchInvoices]
  );

  const fetchCount = useCallback(async () => {
    try {
      const response = await get(api.GET_INVOICE_COUNT);
      setCount(response || {});
    } catch (error) {
      console.error("Error fetching invoice count:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status,
      });
      setCount({});
    }
  }, []);

  useEffect(() => {
    fetchInvoices(searchQuery, currentPage);
    fetchCount();
    fetchCustomers();
  }, [fetchInvoices, fetchCount, searchQuery, currentPage, sortConfig]);

  const summaryData = [
    {
      iconClass: "fas fa-file-invoice-dollar text-primary",
      label: "Total Amount",
      value: count.totalAmount?.toFixed(2) || "0.00",
    },
    {
      iconClass: "fas fa-money-check-alt text-success",
      label: "Paid Amount",
      value: count.amountPaid?.toFixed(2) || "0.00",
    },
    {
      iconClass: "fas fa-check-circle text-success",
      label: "Approved Amount",
      value: count.approvedAmount?.toFixed(2) || "0.00",
    },
    {
      iconClass: "fas fa-exclamation-triangle text-danger",
      label: "Overdue Amount",
      value: count.overDueAmount?.toFixed(2) || "0.00",
    },
    {
      iconClass: "fas fa-times-circle text-danger",
      label: "Rejected Amount",
      value: count.rejectedAmount?.toFixed(2) || "0.00",
    },
  ];

  const getStatusBadgeColor = (status) => {
    if (!status) {
      return { label: "Unknown", color: "secondary" };
    }
    switch (status.toUpperCase()) {
      case "APPROVED":
        return { label: "Approved", color: "success" };
      case "DRAFT":
        return { label: "Draft", color: "secondary" };
      case "REJECTED":
        return { label: "Rejected", color: "danger" };
      case "PAID":
        return { label: "Paid", color: "primary" };
      case "SEND":
      case "SENT":
        return { label: "Sent", color: "info" };
      case "OVERDUE":
        return { label: "Overdue", color: "warning" };
      case "RESUBMITTED":
        return { label: "Re-Submitted", color: "dark" };
      case "PARTIALLY_PAID":
        return { label: "Partially Paid", color: "success" };
      default:
        return { label: status, color: "secondary" };
    }
  };

  return (
    <>
      <div className="page-content">
        <Container fluid className="py-0 px-0">
          <PageSubHeader
            heading="Invoices"
            routePath="Home"
            pageName="Invoices"
          />
          <Row className="px-4">
            <SummaryCards data={summaryData} />

            <Row className="d-flex justify-content-between align-items-center py-2 px-4">
              <Col xs={3}>
                <InputGroup size="sm" className="elbert-search-group">
                  <Input
                    placeholder="Search Invoices"
                    className="elbert-search"
                    value={searchQuery}
                    onChange={handleSearchQuery}
                  />
                  <InputGroupText>
                    <span className="fa fa-search"></span>
                  </InputGroupText>
                </InputGroup>
              </Col>
              <Col xs={3}></Col>
              <Col
                xs={2}
                className="d-flex align-items-center justify-content-end"
              >
                <CustomButton
                  text="+ Add New"
                  onClick={() => navigate("/add-invoice")}
                />
              </Col>
            </Row>
            <Row className="py-2">
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <colgroup>
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "8%" }} />
                          <col style={{ width: "7%" }} />
                        </colgroup>
                        {renderTableHeader()}
                        <tbody>
                          {invoices.map((invoice) => {
                            const { label, color } = getStatusBadgeColor(
                              invoice.status
                            );
                            return (
                              <tr key={invoice.id}>
                                <td style={{ width: "10%" }}>
                                  {invoice.invoiceCode || "N/A"}
                                </td>
                                <td style={{ width: "15%" }}>
                                  {customers.find(
                                    (customer) =>
                                      customer.value ===
                                      String(invoice.toCustomerId)
                                  )?.label || "N/A"}
                                </td>
                                <td style={{ width: "12%" }}>
                                  {formattedDateMonth(invoice.invoiceDate) ||
                                    "N/A"}
                                </td>
                                <td style={{ width: "12%" }}>
                                  {invoice.total?.toFixed(2) || "0.00"}
                                </td>
                                <td style={{ width: "12%" }}>
                                  {invoice.advanceAmount?.toFixed(2) || "0.00"}
                                </td>
                                <td style={{ width: "12%" }}>
                                  {(
                                    invoice.total - (invoice.advanceAmount || 0)
                                  )?.toFixed(2) || "0.00"}
                                </td>
                                <td style={{ width: "12%" }}>
                                  {formattedDateMonth(invoice.dueDate) || "N/A"}
                                </td>
                                <td style={{ width: "10%" }}>
                                  {invoice.currency || "N/A"}
                                </td>
                                <td style={{ width: "8%" }}>
                                  <span className={`badge bg-${color}`}>
                                    {label}
                                  </span>
                                </td>
                                <td style={{ width: "7%" }}>
                                  <div className="d-flex gap-3 align-items-center">
                                    <i
                                      onClick={() =>
                                        navigate(`/view-invoice/${invoice.id}`)
                                      }
                                      className="fa-solid fa-eye action-icon"
                                      title="View"
                                    ></i>
                                    <i
                                      onClick={() =>
                                        navigate(`/add-invoice/${invoice.id}`)
                                      }
                                      className="fa-regular fa-pen-to-square action-icon"
                                      title="Edit"
                                    ></i>
                                    <i
                                      onClick={() => handleViewInvoice(invoice)}
                                      className="fa-solid fa-download action-icon"
                                      title="Download"
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      {invoices.length === 0 && (
                        <div className="text-center py-3">
                          No invoices found.
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Row>
        </Container>
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Invoice Overview</ModalHeader>
        <ModalBody>
          <InvoiceOverviewTemplate invoiceData={selectedInvoice} />
        </ModalBody>
      </Modal>
    </>
  );
};

export default InvoiceLanding;
